import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays, MapPin, ExternalLink, Clock, Loader2, CalendarClock, Rss } from 'lucide-react';
import { NewMeetingButton } from '../components/NewMeetingButton';
import AccessGate from '../components/AccessGate';
import { useAuth } from '../context/AuthContext';

const PAGE_KEY = 'workspace-calendar';

function fmtRange(ev) {
  if (!ev.start) return '';
  const s = new Date(ev.start);
  const opts = { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' };
  if (ev.all_day) return s.toLocaleDateString(undefined, opts);
  const timeOpts = { hour: 'numeric', minute: '2-digit' };
  const e = ev.end ? new Date(ev.end) : null;
  const sameDay = e && s.toDateString() === e.toDateString();
  const base = `${s.toLocaleDateString(undefined, opts)} · ${s.toLocaleTimeString(undefined, timeOpts)}`;
  if (e && sameDay) return `${base} – ${e.toLocaleTimeString(undefined, timeOpts)}`;
  return base;
}

export default function GoogleCalendar() {
  const { getGoogleCalendar, meetingsCalendarEvents, meetingsFeedUrl, user } = useAuth();
  const [data, setData] = useState(null);
  const [meetings, setMeetings] = useState(null);
  const [feed, setFeed] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setError('');
      try {
        const res = await getGoogleCalendar(180);
        if (alive) setData(res);
      } catch (e) {
        if (alive) setError('Could not load the calendar right now.');
      }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Meetings + personal feed load once the signed-in user is known (auth hydrates asynchronously).
  useEffect(() => {
    let alive = true;
    if (!user) { setMeetings(null); setFeed(null); return undefined; }
    meetingsCalendarEvents(365).then((r) => alive && setMeetings(r)).catch(() => alive && setMeetings({ events: [] }));
    meetingsFeedUrl().then((r) => alive && setFeed(r)).catch(() => null);
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  return (
    <AccessGate pageKey={PAGE_KEY}>
      <div className="max-w-4xl mx-auto px-4 py-10 fade-in" data-testid="calendar-page">
        <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
          <div className="flex items-center gap-3">
            <CalendarDays size={28} style={{ color: 'var(--maroon)' }} />
            <h1 className="font-title text-3xl md:text-4xl" style={{ color: 'var(--maroon)' }}>Calendar</h1>
          </div>
          <NewMeetingButton testId="calendar-new-meeting" onSaved={() => meetingsCalendarEvents(365).then(setMeetings).catch(() => null)} />
        </div>
        <div className="h-1 w-20 mb-6" style={{ background: 'var(--gold)' }} />

        {meetings && (
          <section className="mb-8" data-testid="calendar-meetings">
            <div className="flex items-center justify-between gap-2 pb-1 border-b mb-3" style={{ borderColor: 'var(--gold)' }}>
              <h2 className="font-title text-xl flex items-center gap-2" style={{ color: 'var(--navy)' }}><CalendarClock size={18} /> Meetings</h2>
              {feed && <a href={feed.webcal} className="inline-flex items-center gap-1 text-xs hover:underline" style={{ color: 'var(--navy)' }} title="Subscribe to all your meetings in your calendar app" data-testid="calendar-feed-link"><Rss size={12} /> Subscribe (.ics)</a>}
            </div>
            {meetings.events.length === 0 ? (
              <p className="text-sm" style={{ color: 'var(--olive)' }} data-testid="calendar-meetings-empty">No upcoming meetings you're invited to.</p>
            ) : (
              <ul className="space-y-3">
                {meetings.events.map((ev) => (
                  <li key={ev.id} className="bg-white rounded-lg p-4 shadow-sm border" style={{ borderColor: 'rgba(0,0,0,0.08)' }} data-testid={`calendar-meeting-${ev.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <Link to={ev.path} className="font-title text-lg hover:underline" style={{ color: 'var(--maroon)' }} data-testid={`calendar-meeting-link-${ev.id}`}>{ev.summary}</Link>
                        <div className="flex items-center gap-1 text-sm mt-1" style={{ color: 'var(--olive)' }}><Clock size={14} /> {fmtRange(ev)}</div>
                        {ev.location && <div className="flex items-center gap-1 text-sm mt-1" style={{ color: 'var(--olive)' }}><MapPin size={14} /> {ev.location}</div>}
                      </div>
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        <Link to={ev.path} className="inline-flex items-center gap-1 text-sm hover:underline" style={{ color: 'var(--navy)' }}><ExternalLink size={14} /> Meeting page</Link>
                        {ev.gcal_html_link && <a href={ev.gcal_html_link} target="_blank" rel="noreferrer" className="text-xs hover:underline" style={{ color: '#1c6b2c' }}>Google Calendar</a>}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {loading && (
          <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--olive)' }} data-testid="calendar-loading">
            <Loader2 className="animate-spin" size={16} /> Loading events…
          </div>
        )}

        {!loading && error && (
          <p className="text-sm" style={{ color: '#8a1f11' }} data-testid="calendar-error">{error}</p>
        )}

        {!loading && !error && data && !data.configured && !meetings && (
          <div className="bg-white rounded-lg p-6 shadow-sm border text-sm" style={{ borderColor: 'rgba(0,0,0,0.08)', color: 'var(--olive)' }} data-testid="calendar-not-configured">
            The shared calendar hasn’t been set up yet. Please check back soon.
          </div>
        )}

        {!loading && !error && data && data.configured && data.sources.length === 0 && (
          <div className="bg-white rounded-lg p-6 shadow-sm border text-sm" style={{ borderColor: 'rgba(0,0,0,0.08)', color: 'var(--olive)' }} data-testid="calendar-empty">
            There are no calendars available for your membership yet.
          </div>
        )}

        {!loading && !error && data && data.sources.map((src) => (
          <section key={src.id} className="mb-8" data-testid={`calendar-source-${src.id}`}>
            <h2 className="font-title text-xl mb-3 pb-1 border-b" style={{ color: 'var(--navy)', borderColor: 'var(--gold)' }}>{src.label}</h2>
            {src.error && <p className="text-sm" style={{ color: '#8a1f11' }}>{src.error}</p>}
            {!src.error && src.events.length === 0 && (
              <p className="text-sm" style={{ color: 'var(--olive)' }}>No upcoming events.</p>
            )}
            <ul className="space-y-3">
              {src.events.map((ev) => (
                <li key={ev.id} className="bg-white rounded-lg p-4 shadow-sm border" style={{ borderColor: 'rgba(0,0,0,0.08)' }} data-testid={`calendar-event-${ev.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-title text-lg" style={{ color: 'var(--maroon)' }}>{ev.summary}</div>
                      <div className="flex items-center gap-1 text-sm mt-1" style={{ color: 'var(--olive)' }}>
                        <Clock size={14} /> {fmtRange(ev)}
                      </div>
                      {ev.location && (
                        <div className="flex items-center gap-1 text-sm mt-1" style={{ color: 'var(--olive)' }}>
                          <MapPin size={14} /> {ev.location}
                        </div>
                      )}
                      {ev.description && <p className="text-sm mt-2 whitespace-pre-line">{ev.description}</p>}
                    </div>
                    {ev.html_link && (
                      <a href={ev.html_link} target="_blank" rel="noreferrer" className="flex-shrink-0 inline-flex items-center gap-1 text-sm hover:underline" style={{ color: 'var(--navy)' }}>
                        <ExternalLink size={14} /> Open
                      </a>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </AccessGate>
  );
}
